// @flow
import { decorate, observable, action } from 'mobx';
import GWUApi from '../../api';
import playbookStore from '../playbook/PlaybookStore';
import type { Pathway, UserResourceActivity, ResourceActivityType, PlaybookItem } from 'gwu';
import marked from 'marked';
import pathwayPlaybooksData from '../../data/pathways/pathway-playbooks.json';
import pathwayPlaybooksFileToPathMap from '../../data/pathways/pathway-playbook-content-files.js'
import userStore from '../user/UserStore';

const getPathwayResourceIds = (pathway) => {
  const resourceIds = [];
  pathway.topics.forEach(topic => {
    topic.resources.forEach(resource => {
      resourceIds.push(resource.id);
    });
  });

  return resourceIds;
};

const getPathwayResourceActivities = async pathway => {
  const pathwayResourceIds = getPathwayResourceIds(pathway);
  const { data } = await GWUApi.getUserResourceActivities(pathwayResourceIds);

  return data;
};

class PathwayStore {
  pathways: Array<Pathway> = [];
  coachingPathways: Array<Pathway> = [];
  pathway: Pathway;
  pathwayResourceActivites: Array<UserResourceActivity>;
  pathwayPlaybookStaticFileContentMap: Object = {};
  pathwayPlaybookContent: Object = {};

  async getPathways(forceReload = false) {
    if (forceReload || this.pathways.length === 0) {
      const { data } = await GWUApi.getPathways();
      this.pathways = data;
    }
  }

  async getCoachingPathways(forceReload = false) {
    if (forceReload || this.pathways.length === 0) {
      const { data } = await GWUApi.getCoachingPathways();
      this.coachingPathways = data;
    }
  }

  // TODO extract to util (see PlaybookStore)
  async fetchPathwayPlaybookContentByFileName(fileName: string) {
    const existing = this.pathwayPlaybookStaticFileContentMap[fileName];
    if (existing) {
      return existing;
    }

    const filePath = pathwayPlaybooksFileToPathMap[fileName];
    if (!filePath) {
      return;
    }

    const content = await GWUApi.getStaticFileContent(filePath);
    this.pathwayPlaybookStaticFileContentMap[fileName] = content;

    return content;
  }

  async populateRichContentHelper(item: any, prop: string) {
    const value = item[prop];
    if (!value) {
      return;
    }

    if (value.endsWith('.md')) {
      const fileContent = await this.fetchPathwayPlaybookContentByFileName(value);
      item[prop] = marked(fileContent);
    }
  }

  async populatePlaybookItemRichContent(item: PlaybookItem) {
    await this.populateRichContentHelper(item, 'text');
    await this.populateRichContentHelper(item, 'takeaway');
    await this.populateRichContentHelper(item, 'setting');
    await this.populateRichContentHelper(item, 'setup');
    await this.populateRichContentHelper(item, 'technique');
  }

  async getPathwayPlaybookContent(pathway, userLanguage) {
    const pathwayPlaybooksDataByLanguage = pathwayPlaybooksData[userLanguage];
    if (!pathwayPlaybooksDataByLanguage) {
      return;
    }

    const pathwayPlaybookTopicContent = pathwayPlaybooksDataByLanguage.filter(pbd => pbd.pathwayKey === pathway.key);
    if (!pathwayPlaybookTopicContent) {
      return;
    }

    for (let i = 0; i < pathwayPlaybookTopicContent.length; i++) {
      const topicContent = pathwayPlaybookTopicContent[i];
      for (let j = 0; j < topicContent.items.length; j++) {
        const item = topicContent.items[j];
        await this.populatePlaybookItemRichContent(item);
      }
    }

    this.pathwayPlaybookContent[pathway.path] = pathwayPlaybookTopicContent;
  }

  async getPathwayByPath(path: string, userLanguage: string) {
    const { data } = await GWUApi.getPathwayByPath(path);
    const pathway = data;
    await this.getPathwayPlaybookContent(pathway, userLanguage);
    await playbookStore.getPlaybookByPathwayKey(pathway.key, userLanguage);
    this.pathwayResourceActivites = await getPathwayResourceActivities(pathway);
    this.pathway = pathway; // setting this last since we can check what is loaded by id
  }

  getPathwayTopicPlaybookContentItems(pathwayPath: string, topicKey: srting) {
    const pathwayTopicPlaybookContent = this.pathwayPlaybookContent[pathwayPath];
    if (!pathwayTopicPlaybookContent) {
      return [];
    }

    const topicPlaybookContent = pathwayTopicPlaybookContent.find(t => t.topicKey === topicKey);
    if (!topicPlaybookContent) {
      return [];
    }

    return topicPlaybookContent.items;
  }

  async recordPathwayResourceActivity(resourceId: number, activity: ResourceActivityType) {
    if (userStore.isAnonymous) {
      return;
    }

    const { data } = await GWUApi.recordUserResourceActivity(resourceId, activity);
    const updatedActivity = data;
    const existingActivityIndex = this.pathwayResourceActivites.findIndex(a => a.id === updatedActivity.id);
    if (existingActivityIndex === -1) {
      this.pathwayResourceActivites.push(updatedActivity);
    } else {
      this.pathwayResourceActivites.splice(existingActivityIndex, 1, updatedActivity);
    }
  }

  async sendPathwaySuggestion(comment: string, commentType: string) {
    await GWUApi.sendPathwaySuggestion(comment, commentType);
  }
}

decorate(PathwayStore, {
  pathways: observable,
  coachingPathways: observable,
  pathway: observable,
  pathwayResourceActivites: observable,
  pathwayPlaybookContent: observable,
  getPathways: action,
  getPathwayByPath: action
});

export default new PathwayStore();



// WEBPACK FOOTER //
// ./src/modules/pathway/PathwayStore.js